import { PDFDocument, PDFFont, PDFImage, PDFPage, StandardFonts } from 'pdf-lib';
import { Color, rgb, cmyk } from 'pdf-lib';

import fontkit from 'pdf-fontkit';

export class PdfGen {
  private fontCache: Map<string, PDFFont> = new Map<string, PDFFont>();
  private imageCache: Map<string, PDFImage> = new Map<string, PDFImage>();

  private constructor(
    public readonly pdf: PDFDocument,
    public readonly gs: PageGen[]
  ) {}

  public g1: PageGen = null as any;
  public g2: PageGen = null as any;
  public g3: PageGen = null as any;
  public g0: PageGen = null as any;

  public static async create(pageCount = 0, landscape = false): Promise<PdfGen> {
    const pdf = await PDFDocument.create();
    pdf.registerFontkit(fontkit);

    const pdfGen = new PdfGen(pdf, []);

    for (let i = 0; i < pageCount; i++) {
      pdfGen.addPage(landscape);
    }

    return pdfGen;
  }

  public async initialize(): Promise<void> {
    await this.addStandardFonts();
    await this.addSymbolFont();
  }

  public async embedImage(
    key: string,
    imageBytes: Uint8Array,
    type: 'png' | 'jpeg'
  ): Promise<PDFImage> {
    const cacheKey = `${key}:${type}`;

    const cached = this.imageCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let pdfImage: PDFImage;
    if (type === 'png') {
      pdfImage = await this.pdf.embedPng(imageBytes);
    } else {
      pdfImage = await this.pdf.embedJpg(imageBytes);
    }

    this.imageCache.set(cacheKey, pdfImage);
    return pdfImage;
  }

  public clearImageCache(): void {
    this.imageCache.clear();
  }

  public addPage(landscape = false): PageGen {
    const size = PdfSize.A4.toNumberArray();
    const page = this.pdf.addPage(landscape ? [size[1], size[0]] : size);

    const pageGen = new PageGen(this, page);
    this.gs.push(pageGen);

    if (this.gs.length <= 1) this.g1 = this.gs[0];
    if (this.gs.length <= 2) this.g2 = this.gs[1];
    if (this.gs.length <= 3) this.g3 = this.gs[2];

    this.g0 = pageGen;

    return pageGen;
  }

  public getPage(index: number): PageGen {
    if (index < 0 || index >= this.gs.length) {
      throw new Error(`Page index ${index} out of bounds (0-${this.gs.length - 1})`);
    }
    return this.gs[index];
  }

  public async addFont(
    font: string,
    fontData: StandardFonts | Uint8Array,
    subset = false
  ): Promise<void> {
    const f = await this.pdf.embedFont(fontData, { subset });
    this.fontCache.set(font, f);
  }

  public async addStandardFonts(): Promise<void> {
    // Load NotoSans font for Unicode character support (superscript, subscript, etc.)
    // Use subsetting to reduce PDF size - only embed used characters
    try {
      const fontLoader = getFontLoader();
      const fontBytes = await fontLoader.loadFont('/font/NotoSans-Regular.ttf');
      await this.addFont(PdfFont.FONT_NAME, fontBytes, true);
    } catch (error) {
      console.warn('Failed to load NotoSans font, falling back to Helvetica:', error);
      await this.addFont(PdfFont.FONT_NAME, StandardFonts.Helvetica);
    }
    try {
      const fontLoader = getFontLoader();
      const boldBytes = await fontLoader.loadFont('/font/NotoSans-Bold.ttf');
      await this.addFont(PdfFont.BOLD_FONT_NAME, boldBytes, true);
    } catch (error) {
      console.warn('Failed to load NotoSans-Bold font, falling back to HelveticaBold:', error);
      await this.addFont(PdfFont.BOLD_FONT_NAME, StandardFonts.HelveticaBold);
    }
    try {
      const fontLoader = getFontLoader();
      const italicBytes = await fontLoader.loadFont('/font/NotoSans-Italic.ttf');
      await this.addFont(PdfFont.ITALIC_FONT_NAME, italicBytes, true);
    } catch (error) {
      console.warn('Failed to load NotoSans-Italic font, falling back to HelveticaOblique:', error);
      await this.addFont(PdfFont.ITALIC_FONT_NAME, StandardFonts.HelveticaOblique);
    }
    try {
      const fontLoader = getFontLoader();
      const monoBytes = await fontLoader.loadFont('/font/NotoSansMono-Regular.ttf');
      await this.addFont(PdfFont.MONO_FONT_NAME, monoBytes, true);
    } catch (error) {
      console.warn('Failed to load NotoSansMono font, falling back to Courier:', error);
      await this.addFont(PdfFont.MONO_FONT_NAME, StandardFonts.Courier);
    }
  }

  public async addSymbolFont(): Promise<void> {
    try {
      const fontLoader = getFontLoader();
      const fontBytes = await fontLoader.loadFont('/font/EduFrameSymbols.ttf');
      await this.addFont(PdfFont.SYMBOL_FONT_NAME, fontBytes, true);
    } catch (error) {
      throw new Error(`Failed to load EduFrameSymbols font: ${error}`);
    }
  }

  public getFont(font: string): PDFFont {
    const f = this.fontCache.get(font);
    if (!f) throw new Error(`Font not found: ${font}`);
    return f;
  }

  public async save(): Promise<Uint8Array> {
    return await this.pdf.save();
  }
}

export class PageGen {
  public constructor(
    public readonly pdf: PdfGen,
    public readonly page: PDFPage
  ) {}

  public getXY(point: PdfPoint): { x: number; y: number } {
    return { x: point.x, y: this.page.getHeight() - point.y };
  }
}

export class PdfImage {
  public constructor(
    public readonly image: PDFImage,
    public readonly width: number,
    public readonly height: number,
    /** Decoded pixel canvas — available if image was embedded via RawImage.embed() or preprocessImages(). */
    public readonly canvas?: import('./imageCanvas').IImageCanvas
  ) {}
}

export class PdfFont {
  public static FONT_NAME = 'NotoSans';
  public static BOLD_FONT_NAME = 'NotoSans-Bold';
  public static ITALIC_FONT_NAME = 'NotoSans-Italic';
  public static MONO_FONT_NAME = 'NotoSansMono';
  public static SYMBOL_FONT_NAME = 'EduFrameSymbols';

  public constructor(
    public readonly font: string,
    public readonly size: number
  ) {}

  public static BaseFont: PdfFont = new PdfFont(PdfFont.FONT_NAME, 24);
  public static Symbols = new PdfFont(PdfFont.SYMBOL_FONT_NAME, 24);
}

export type PdfColorModel = 'rgb' | 'cmyk';

export class PdfColor {
  private model: PdfColorModel = 'rgb';
  private r?: number; // 0-255
  private g?: number; // 0-255
  private b?: number; // 0-255
  private c?: number; // 0-1
  private m?: number; // 0-1
  private y?: number; // 0-1
  private k?: number; // 0-1

  public opacity = 1; // 0-1

  public static fromRgb(r: number, g: number, b: number, opacity = 1): PdfColor {
    const color = new PdfColor();
    color.model = 'rgb';
    color.r = r;
    color.g = g;
    color.b = b;
    color.opacity = opacity;
    return color;
  }

  public static fromColor(source: PdfColor, opacity = 1): PdfColor {
    if (source.model === 'cmyk') {
      return PdfColor.fromCmyk(source.c || 0, source.m || 0, source.y || 0, source.k || 0, opacity);
    }

    return PdfColor.fromRgb(source.r || 0, source.g || 0, source.b || 0, opacity);
  }

  public static fromCmyk(c: number, m: number, y: number, k: number, opacity = 1): PdfColor {
    const color = new PdfColor();
    color.model = 'cmyk';
    color.c = c;
    color.m = m;
    color.y = y;
    color.k = k;
    color.opacity = opacity;
    return color;
  }

  public static fromHex(hex: string, opacity = 1): PdfColor {
    if (!hex.startsWith('#')) throw new Error(`Invalid hex color: ${hex}`);
    if (hex.length === 7) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return PdfColor.fromRgb(r, g, b, opacity);
    } else if (hex.length === 4) {
      const r = parseInt(hex[1] + hex[1], 16);
      const g = parseInt(hex[2] + hex[2], 16);
      const b = parseInt(hex[3] + hex[3], 16);
      return PdfColor.fromRgb(r, g, b, opacity);
    }
    throw new Error(`Invalid hex color: ${hex}`);
  }

  public static fromHsl(h: number, s: number, l: number, opacity = 1): PdfColor {
    // Normalize H, S, L
    h = ((h % 360) + 360) % 360;
    s = Math.max(0, Math.min(1, s));
    l = Math.max(0, Math.min(1, l));

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r1 = 0,
      g1 = 0,
      b1 = 0;

    if (h < 60) {
      r1 = c;
      g1 = x;
      b1 = 0;
    } else if (h < 120) {
      r1 = x;
      g1 = c;
      b1 = 0;
    } else if (h < 180) {
      r1 = 0;
      g1 = c;
      b1 = x;
    } else if (h < 240) {
      r1 = 0;
      g1 = x;
      b1 = c;
    } else if (h < 300) {
      r1 = x;
      g1 = 0;
      b1 = c;
    } else {
      r1 = c;
      g1 = 0;
      b1 = x;
    }

    const r = Math.round((r1 + m) * 255);
    const g = Math.round((g1 + m) * 255);
    const b = Math.round((b1 + m) * 255);

    return PdfColor.fromRgb(r, g, b, opacity);
  }

  public toPdfLibColor(): Color {
    if (this.model === 'rgb') {
      return rgb(this.r! / 255, this.g! / 255, this.b! / 255);
    } else if (this.model === 'cmyk') {
      return cmyk(this.c!, this.m!, this.y!, this.k!);
    }
    throw new Error('Color model not set');
  }

  public toPdfLibOpacity(): number {
    return this.opacity;
  }

  public toRgbaArray(): [number, number, number, number] {
    const [r, g, b] =
      this.model === 'rgb'
        ? [this.r! / 255, this.g! / 255, this.b! / 255]
        : [
            1 - Math.min(1, this.c! * (1 - this.k!) + this.k!),
            1 - Math.min(1, this.m! * (1 - this.k!) + this.k!),
            1 - Math.min(1, this.y! * (1 - this.k!) + this.k!),
          ];
    return [r, g, b, this.opacity];
  }

  public toHexString(): string {
    if (this.model !== 'rgb') throw new Error('Hex only supported for RGB');
    const r = this.r!.toString(16).padStart(2, '0');
    const g = this.g!.toString(16).padStart(2, '0');
    const b = this.b!.toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  public getModel(): PdfColorModel {
    return this.model;
  }

  /** Get RGB values as 0-255 range (for RGB colors) */
  public getRgb(): { r: number; g: number; b: number } {
    if (this.model === 'rgb') {
      return { r: this.r!, g: this.g!, b: this.b! };
    }
    // Convert CMYK to RGB
    const r = 255 * (1 - this.c!) * (1 - this.k!);
    const g = 255 * (1 - this.m!) * (1 - this.k!);
    const b = 255 * (1 - this.y!) * (1 - this.k!);
    return { r: Math.round(r), g: Math.round(g), b: Math.round(b) };
  }
}

export class PdfPen {
  public dashArray: number[] | null = null;

  public constructor(
    public readonly color: PdfColor,
    public readonly thickness: number
  ) {}
}

export class PdfBrush {
  public constructor(public readonly color: PdfColor) {}

  public static Black = new PdfBrush(PdfColor.fromRgb(0, 0, 0));
  public static White = new PdfBrush(PdfColor.fromRgb(255, 255, 255));
  public static LightGray = new PdfBrush(PdfColor.fromRgb(211, 211, 211));
  public static LightSkyBlue = new PdfBrush(PdfColor.fromRgb(135, 206, 250));
  public static Red = new PdfBrush(PdfColor.fromRgb(255, 0, 0));
}

export class PdfPoint {
  public constructor(
    public readonly x: number,
    public readonly y: number
  ) {}

  public static zero: PdfPoint = new PdfPoint(0, 0);

  public move(vector: PdfVector): PdfPoint {
    return new PdfPoint(this.x + vector.dx, this.y + vector.dy);
  }
}

export class PdfVector {
  public constructor(
    public readonly dx: number,
    public readonly dy: number
  ) {}

  public static zero: PdfVector = new PdfVector(0, 0);

  public move(dx: number, dy: number): PdfVector {
    return new PdfVector(this.dx + dx, this.dy + dy);
  }
}

export class PdfUnit {
  public static MM = 72 / 25.4;
  public static FromMillimeters(mm: number): number {
    return mm * PdfUnit.MM;
  }
}

export class PdfSize {
  public constructor(
    public readonly width: number,
    public readonly height: number
  ) {}
  public static A4: PdfSize = new PdfSize(
    PdfUnit.FromMillimeters(210),
    PdfUnit.FromMillimeters(297)
  );
  public static A4H: PdfSize = new PdfSize(
    PdfUnit.FromMillimeters(297),
    PdfUnit.FromMillimeters(210)
  );

  public toNumberArray(): [number, number] {
    return [this.width, this.height];
  }
}

export class PdfRect {
  public readonly left: number;
  public readonly right: number;
  public readonly top: number;
  public readonly bottom: number;
  public readonly centerX: number;
  public readonly centerY: number;
  public readonly topLeft: PdfPoint;
  public readonly topRight: PdfPoint;
  public readonly bottomLeft: PdfPoint;
  public readonly bottomRight: PdfPoint;
  public readonly size: PdfSize;

  public constructor(
    public readonly x: number,
    public readonly y: number,
    public readonly width: number,
    public readonly height: number
  ) {
    this.left = x;
    this.top = y;
    this.right = x + width;
    this.bottom = y + height;
    this.centerX = x + width / 2;
    this.centerY = y + height / 2;

    // Initialize computed points in constructor (after x, y, width, height are assigned)
    this.topLeft = new PdfPoint(this.x, this.y);
    this.topRight = new PdfPoint(this.x + this.width, this.y);
    this.bottomLeft = new PdfPoint(this.x, this.y + this.height);
    this.bottomRight = new PdfPoint(this.x + this.width, this.y + this.height);
    this.size = new PdfSize(this.width, this.height);
  }

  public static fromPoints(p1: PdfPoint, p2: PdfPoint): PdfRect {
    const x = Math.min(p1.x, p2.x);
    const y = Math.min(p1.y, p2.y);
    const width = Math.abs(p2.x - p1.x);
    const height = Math.abs(p2.y - p1.y);
    return new PdfRect(x, y, width, height);
  }

  public static fromPointAndSize(point: PdfPoint, size: PdfSize): PdfRect {
    return new PdfRect(point.x, point.y, size.width, size.height);
  }

  public static circle(center: PdfPoint, d: number): PdfRect {
    return new PdfRect(center.x - d / 2, center.y - d / 2, d, d);
  }

  public static ellipse(center: PdfPoint, dX: number, dY: number): PdfRect {
    return new PdfRect(center.x - dX / 2, center.y - dY / 2, dX, dY);
  }

  public move(dx: number, dy: number): PdfRect {
    return new PdfRect(this.x + dx, this.y + dy, this.width, this.height);
  }

  public inflate(amount: number): PdfRect {
    return new PdfRect(
      this.x - amount,
      this.y - amount,
      this.width + amount * 2,
      this.height + amount * 2
    );
  }

  public deflate(amount: number): PdfRect {
    return new PdfRect(
      this.x + amount,
      this.y + amount,
      this.width - amount * 2,
      this.height - amount * 2
    );
  }

  public centerSquare(): PdfRect {
    const sz = Math.min(this.width, this.height);
    return new PdfRect(this.x + (this.width - sz) / 2, this.y + (this.height - sz) / 2, sz, sz);
  }

  public centerRect(size: PdfSize): PdfRect {
    const w1 = this.width;
    const h1 = (this.width / size.width) * size.height;
    const w2 = (this.height / size.height) * size.width;
    const h2 = this.height;

    if (h1 < this.height) {
      return new PdfRect(this.x, this.y + (this.height - h1) / 2, w1, h1);
    } else {
      return new PdfRect(this.x + (this.width - w2) / 2, this.y, w2, h2);
    }
  }
}

export enum ETextAlign {
  BottomLeft,
  BottomCenter,
  BottomRight,
  CenterLeft,
  Center,
  CenterRight,
  TopLeft,
  TopCenter,
  TopRight,
  LeftBaseline,
  CenterBaseline,
  RightBaseline,
}

/**
 * Unified file cache interface - handles fonts, archives, and other static files
 * Implemented differently for Node.js (filesystem) and browser (Cache API)
 */
export interface IFileCache {
  loadFont(url: string): Promise<Uint8Array>;
  loadArchive(name: string): Promise<Uint8Array>;
  preload?(): Promise<void>;
}

let fileCacheInstance: IFileCache | null = null;

export function setFileCache(cache: IFileCache): void {
  fileCacheInstance = cache;
}

export function getFileCache(): IFileCache {
  if (!fileCacheInstance) {
    throw new Error('FileCache not set. Call setFileCache() before using PDF generation.');
  }
  return fileCacheInstance;
}

export function getFontLoader(): { loadFont: (url: string) => Promise<Uint8Array> } {
  return {
    loadFont: (url: string) => getFileCache().loadFont(url),
  };
}
